/* v3 — Bento Asymmetric
   2-column bento. Briefing dominates left. Approvals stacked right.
   Activity flows below briefing. Roles full-width with one lead tile.
   Today rail = horizontal timeline at the bottom. */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ExternalLink, FileCheck2, FileSearch, Mail, Mic, UserCheck, Video } from 'lucide-react';
import { useDashboardData, buildTaskRoute } from '../useDashboardData';
import type { TaskItem } from '@/modules/shared/types/yc-demo';
import type { JobPosting } from '@/modules/job/services/job.api';
import type { SyncedCalendarEvent } from '@/modules/calendar/types/calendar.types';
import { VariantSwitcher } from './VariantSwitcher';
import styles from './home-v3.module.css';

function greetingFor(d: Date) {
    const h = d.getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
}

function formatTime(iso: string) {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function relativeAgo(ms: number) {
    const m = Math.max(1, Math.round(ms / 60_000));
    if (m < 60) return `${m}m ago`;
    return `${Math.round(m / 60)}h ago`;
}

function relativeFrom(iso: string) {
    return relativeAgo(Date.now() - new Date(iso).getTime());
}

function hash(s: string) {
    return s.split('').reduce((a, c) => (a * 31 + c.charCodeAt(0)) & 0xffff, 7);
}

function buildBriefing(userName: string, jobs: JobPosting[], tasks: TaskItem[], events: SyncedCalendarEvent[]) {
    const greeting = `${greetingFor(new Date())}, ${userName.split(' ')[0]}.`;
    const primary = jobs[0];
    const secondary = jobs[1];
    const high = tasks.filter((t) => t.priority === 'high');
    const next = events[0];
    const sourced = primary ? 31 + (hash(primary._id) % 32) : 0;
    const screened = Math.max(8, Math.round(sourced * 0.6));
    const passed = Math.max(high.length, Math.round(screened * 0.25));

    if (jobs.length === 0 && tasks.length === 0) {
        return { greeting, paragraph: 'Quiet morning. Nothing pending.' };
    }

    const lines: string[] = [];
    if (primary) {
        lines.push(
            `Sourced ${sourced} candidates for **${primary.title}** overnight, screened ${screened}, ${passed} passed my bar.${high.length > 0 ? ` ${high.length} need your judgment.` : ''}`,
        );
    }
    if (secondary) {
        const drafted = 8 + (hash(secondary._id) % 9);
        lines.push(`Pre-drafted outreach for ${drafted} warm referrals on **${secondary.title}**.`);
    }
    if (next) {
        const who = next.attendees?.[0]?.name?.split(' ')[0] || 'Someone';
        lines.push(`${who} confirmed at ${formatTime(next.startDateTime)}.`);
    }
    return { greeting, paragraph: lines.join(' ') };
}

function renderBold(text: string) {
    return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
        p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>,
    );
}

function renderActivity(text: string) {
    return text.split(/(\*[^*]+\*)/g).map((p, i) =>
        p.startsWith('*') && p.endsWith('*') ? <em key={i}>{p.slice(1, -1)}</em> : <span key={i}>{p}</span>,
    );
}

const ICON_BY = {
    sourcing: { Comp: FileSearch, cls: styles.iconSourcing },
    screening: { Comp: FileCheck2, cls: styles.iconScreening },
    nurture: { Comp: Mail, cls: styles.iconNurture },
    interview: { Comp: Mic, cls: styles.iconInterview },
    human: { Comp: UserCheck, cls: styles.iconHuman },
};

function initials(name: string) {
    const p = name.trim().split(/\s+/);
    if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
    return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

export function HomeV3() {
    const { state, userName } = useDashboardData();
    const navigate = useNavigate();

    const briefing = useMemo(() => {
        if (state.phase !== 'ready') return null;
        return buildBriefing(userName, state.data.jobs, state.data.tasks, state.data.events);
    }, [state, userName]);

    if (state.phase === 'loading') {
        return (
            <main className={styles.page}>
                <div className={styles.container}>
                    <VariantSwitcher current="v3" className={styles.variantBar} activeClass={styles.active} />
                    <div className={styles.grid}>
                        <div className={styles.cellHero}><div className={styles.skeletonHero} /></div>
                        <div className={styles.cellApprovals}><div className={styles.skeletonCard} /></div>
                    </div>
                </div>
            </main>
        );
    }
    if (state.phase === 'error') {
        return (
            <main className={styles.page}>
                <div className={styles.container}>
                    <div className={styles.errorBox}>{state.message}</div>
                </div>
            </main>
        );
    }

    const { jobs, tasks, events, activity, lastActionAgoMs } = state.data;
    const high = tasks.filter((t) => t.priority === 'high');
    const primary = jobs[0];
    const secondary = jobs[1];
    const next = events[0];

    const chips: Array<{ label: string; href: string; primary?: boolean }> = [];
    if (high.length > 0) chips.push({ label: `${high.length} need your judgment`, href: '/v2/jobs', primary: true });
    if (next) chips.push({ label: `${next.attendees?.[0]?.name?.split(' ')[0] || 'Next'} at ${formatTime(next.startDateTime)}`, href: '/v2/calendar' });
    if (secondary) chips.push({ label: `Approve outreach`, href: `/v2/jobs/${secondary._id}/plan` });
    chips.push({ label: 'Lantern brain', href: '/brain' });

    return (
        <main className={styles.page}>
            <div className={styles.container}>
                <VariantSwitcher current="v3" className={styles.variantBar} activeClass={styles.active} />

                <div className={styles.grid}>
                    {/* HERO */}
                    <section className={`${styles.hero} ${styles.cellHero}`}>
                        <div className={styles.heroPattern} aria-hidden />
                        <div className={styles.heroIdent}>
                            <div className={styles.heroAvatar}>
                                <img src="/avatars/jacinta.svg" alt="" />
                            </div>
                            <div className={styles.heroIdentText}>
                                <span className={styles.heroName}>Jacinta</span>
                                <span className={styles.heroStatus}>
                                    <span className={styles.heroDot} aria-hidden />
                                    last action {relativeAgo(lastActionAgoMs)}
                                </span>
                            </div>
                        </div>
                        <h1 className={styles.heroGreeting}>{briefing?.greeting}</h1>
                        <p className={styles.heroParagraph}>{briefing && renderBold(briefing.paragraph)}</p>
                        <div className={styles.chips}>
                            {chips.slice(0, 4).map((c, i) => (
                                <button
                                    key={c.href + i}
                                    className={`${styles.chip} ${c.primary ? styles.chipPrimary : ''}`}
                                    onClick={() => navigate(c.href)}
                                >
                                    {c.label}
                                    <ArrowRight size={11} strokeWidth={2.25} />
                                </button>
                            ))}
                        </div>
                    </section>

                    {/* APPROVALS (right rail) */}
                    <section className={`${styles.approvals} ${styles.cellApprovals}`}>
                        <div className={styles.cardHead}>
                            <h2 className={styles.cardTitle}>Pending approvals</h2>
                            <span className={styles.cardSub}>{tasks.length} waiting</span>
                        </div>
                        <div className={styles.approvalList}>
                            {tasks.slice(0, 6).map((task) => {
                                const cls =
                                    task.priority === 'high'
                                        ? styles.priorityHigh
                                        : task.priority === 'low'
                                          ? styles.priorityLow
                                          : styles.priorityNormal;
                                return (
                                    <button key={task._id} className={styles.approvalRow} onClick={() => navigate(buildTaskRoute(task))}>
                                        <div className={styles.approvalAvatar}>{initials(task.candidateName)}</div>
                                        <div className={styles.approvalIdent}>
                                            <div className={styles.approvalName}>{task.candidateName}</div>
                                            <div className={styles.approvalSub}>{task.jobTitle} · {task.kind === 'cv_review' ? 'CV' : 'Interview'}</div>
                                        </div>
                                        <span className={`${styles.priorityDot} ${cls}`} aria-label={task.priority} />
                                    </button>
                                );
                            })}
                            {tasks.length === 0 && <div className={styles.empty}>None pending.</div>}
                        </div>
                    </section>

                    {/* ACTIVITY (left bottom) */}
                    <section className={`${styles.activity} ${styles.cellActivity}`}>
                        <div className={styles.cardHead}>
                            <h2 className={styles.cardTitle}>Recent activity</h2>
                            <span className={styles.cardSub}>{activity.length} events</span>
                        </div>
                        <div className={styles.activityList}>
                            {activity.slice(0, 6).map((ev) => {
                                const ic = ICON_BY[ev.iconName];
                                const Comp = ic.Comp;
                                return (
                                    <div key={ev.id} className={styles.activityRow}>
                                        <div className={`${styles.activityIcon} ${ic.cls}`}>
                                            <Comp size={13} strokeWidth={1.75} />
                                        </div>
                                        <div className={styles.activityText}>{renderActivity(ev.text)}</div>
                                        <div className={styles.activityTime}>{relativeFrom(ev.at)}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    {/* ROLES (full width) */}
                    <section className={`${styles.roles} ${styles.cellRoles}`}>
                        <div className={styles.cardHead}>
                            <h2 className={styles.cardTitle}>Active roles</h2>
                            <span className={styles.cardSub}>{jobs.length} open</span>
                        </div>
                        <div className={styles.rolesGrid}>
                            {jobs.slice(0, 4).map((job, idx) => {
                                const seed = hash(job._id);
                                const sourced = 35 + (seed % 23);
                                const screened = 9 + (seed % 8);
                                const interview = 3 + (seed % 4);
                                const offer = seed % 3;
                                return (
                                    <button
                                        key={job._id}
                                        className={`${styles.roleCard} ${idx === 0 ? styles.roleCardLead : ''}`}
                                        onClick={() => navigate(`/v2/jobs/${job._id}/plan`)}
                                    >
                                        <div>
                                            <div className={styles.roleTitle}>{job.title}</div>
                                            <div className={styles.roleMeta}>{job.department || job.location || 'Workspace'}</div>
                                        </div>
                                        <div className={styles.roleStat}>
                                            <span className={styles.roleStatNum}>{sourced}</span>
                                            <span className={styles.roleStatLabel}>sourced today</span>
                                        </div>
                                        <div className={styles.miniFunnel}>
                                            <span><strong>{screened}</strong> screened</span>
                                            <span className={styles.miniFunnelDivider}>·</span>
                                            <span><strong>{interview}</strong> interview</span>
                                            <span className={styles.miniFunnelDivider}>·</span>
                                            <span><strong>{offer}</strong> offer</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    {/* TODAY RAIL */}
                    <section className={`${styles.todayRail} ${styles.cellCalendar}`}>
                        <div className={styles.cardHead}>
                            <h2 className={styles.cardTitle}>Today</h2>
                            <span className={styles.cardSub}>{events.length === 0 ? 'no events' : `${events.length} upcoming`}</span>
                        </div>
                        <div className={styles.railTrack}>
                            {events.slice(0, 5).map((ev, i) => {
                                const who =
                                    ev.attendees?.[0]?.name || ev.attendees?.[0]?.email?.split('@')[0] || ev.organizer?.name || '';
                                return (
                                    <div key={ev._id} className={`${styles.railSlot} ${i === 0 ? styles.railSlotNext : ''}`}>
                                        <div className={styles.railTime}>{formatTime(ev.startDateTime)}</div>
                                        <div className={styles.railTitle}>{ev.title}</div>
                                        {who && <div className={styles.railSub}>{who}</div>}
                                        {ev.meetingUrl ? (
                                            <a className={styles.railCta} href={ev.meetingUrl} target="_blank" rel="noopener noreferrer">
                                                <Video size={11} strokeWidth={2} /> Join
                                            </a>
                                        ) : (
                                            <button className={`${styles.railCta} ${styles.railCtaGhost}`} onClick={() => navigate('/v2/calendar')}>
                                                <ExternalLink size={11} strokeWidth={2} /> Open
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                            {events.length === 0 && <div className={styles.empty}>Nothing scheduled.</div>}
                        </div>
                    </section>
                </div>
            </div>
        </main>
    );
}
